import React, { useEffect, useRef, useState } from "react"
import { useWatch } from "antd/es/form/Form"
import PropTypes from "prop-types"
import { Card } from "ui/Card"
import { Form, Flex, Input, Modal, Button } from "antd"
import { useTranslation } from "react-i18next"
import { MultipleSelectWithAddItemOption } from "ui/MultipleSelect/MultipleSelectWithAddItemOption"
import { CheckableTag } from "ui/CheckableTag"
import { useDispatch, useSelector } from "react-redux"
import { getExperienceOptions, addExperienceOption } from "store/trips"
import { Text } from "ui/Typography"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import styles from "./ExperienceDetails.module.css"

export const ExperienceDetails = ({ form }) => {
  const [t] = useTranslation()
  const dispatch = useDispatch()
  const { isMobile } = useDevice()
  const { data: experienceOptionsData, loading: isExperienceOptionsLoading } = useSelector(
    (state) => state.trips.experienceOptions
  )

  const formLanguages = useWatch("languages", form) || []
  const formSkillLevels = useWatch("skill_levels", form) || []
  const watchedIncludedItems = useWatch("included_items", form) || []
  const watchedBringItems = useWatch("bring_items", form) || []

  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)
  const [newLanguage, setNewLanguage] = useState("")
  const [isAddingLanguage, setIsAddingLanguage] = useState(false)
  const [isSkillModalOpen, setIsSkillModalOpen] = useState(false)
  const [newSkillLevel, setNewSkillLevel] = useState("")
  const [isAddingSkillLevel, setIsAddingSkillLevel] = useState(false)
  const languageInputRef = useRef(null)
  const skillInputRef = useRef(null)

  useEffect(() => {
    dispatch(getExperienceOptions())
  }, [dispatch])

  // Auto-select English when options load and no languages are set yet
  useEffect(() => {
    const languages = experienceOptionsData?.languages || []
    if (languages.length === 0 || formLanguages.length > 0) return
    const english = languages.find((lang) => lang.name?.toLowerCase() === "english")
    if (english) form.setFieldValue("languages", [english.id])
  }, [experienceOptionsData?.languages])

  const handleLanguageChange = (language, checked) => {
    const next = checked ? [...formLanguages, language.id] : formLanguages.filter((l) => l !== language.id)
    form.setFieldValue("languages", next)
  }

  const handleSkillLevelChange = (level, checked) => {
    const next = checked ? [...formSkillLevels, level.id] : formSkillLevels.filter((l) => l !== level.id)
    form.setFieldValue("skill_levels", next)
  }

  const handleIncludedItemsChange = (values) => {
    form.setFieldsValue({ included_items: values })
  }

  const handleBringItemsChange = (values) => {
    form.setFieldsValue({ bring_items: values })
  }

  // Create an option, refetch the list, then auto-select the new option. Uses the id from the
  // add response ({ data: { item: { id, name } } }) — a name match alone picks the wrong option
  // when that name already exists — falling back to the newest same-named option.
  const addOptionAndSelect = async (itemType, fieldName, name) => {
    const addResponse = await dispatch(addExperienceOption(itemType, name))
    const optionsResponse = await dispatch(getExperienceOptions())
    const options = optionsResponse?.data?.[fieldName] || []
    const sameNamedOptions = options.filter((option) => option.name?.toLowerCase() === name.toLowerCase())
    const newOptionId = addResponse?.data?.item?.id ?? sameNamedOptions[sameNamedOptions.length - 1]?.id
    if (!newOptionId) return
    const selectedIds = form.getFieldValue(fieldName) || []
    if (!selectedIds.includes(newOptionId)) form.setFieldValue(fieldName, [...selectedIds, newOptionId])
  }

  const handleAddLanguage = () => {
    const name = newLanguage.trim()
    if (!name) return
    setIsAddingLanguage(true)
    addOptionAndSelect("language", "languages", name)
      .finally(() => {
        setNewLanguage("")
        setIsLanguageModalOpen(false)
        setIsAddingLanguage(false)
      })
  }

  const handleAddSkillLevel = () => {
    const name = newSkillLevel.trim()
    if (!name) return
    setIsAddingSkillLevel(true)
    addOptionAndSelect("skill_level", "skill_levels", name)
      .finally(() => {
        setNewSkillLevel("")
        setIsSkillModalOpen(false)
        setIsAddingSkillLevel(false)
      })
  }

  // Renders the "add option" dialog as a Modal on desktop and a bottom drawer on mobile.
  // CustomDrawer has no footer, so on mobile the input and the submit button live in the body.
  const renderAddOptionDialog = ({ title, open, onClose, onSubmit, value, onChange, placeholder, inputRef, loading }) => {
    const input = (
      <Input
        ref={inputRef}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPressEnter={onSubmit}
      />
    )

    if (isMobile) {
      return (
        <CustomDrawer open={open} setOpen={() => onClose()} title={title}>
          <Flex vertical gap="middle" className={styles.drawerBody}>
            {input}
            <Button
              type="primary"
              block
              size="large"
              onClick={onSubmit}
              disabled={!value.trim()}
              loading={loading}
            >
              {t("experiences.details.labels.add_item")}
            </Button>
          </Flex>
        </CustomDrawer>
      )
    }

    return (
      <Modal
        title={title}
        open={open}
        onOk={onSubmit}
        onCancel={onClose}
        okText={t("experiences.details.labels.add_item")}
        okButtonProps={{ disabled: !value.trim(), loading }}
        afterOpenChange={(isOpen) => { if (isOpen) inputRef.current?.focus() }}
        destroyOnClose
      >
        {input}
      </Modal>
    )
  }

  return (
    <Card title={t("experiences.details.experience_details")} className="mb-3xl">
      <Form.Item name="included_items" label={t("experiences.details.labels.trip_items")}>
        <MultipleSelectWithAddItemOption
          placeholder={t("experiences.details.placeholders.select_or_type")}
          options={experienceOptionsData?.included_items}
          onChange={handleIncludedItemsChange}
          loading={isExperienceOptionsLoading}
          addItemLabel={t("experiences.details.labels.add_item")}
          notFoundLabel={t("experiences.details.labels.not_found")}
          disabledOptions={watchedBringItems}
          onAddItem={(name) => dispatch(addExperienceOption("included_item", name))}
        />
      </Form.Item>

      <Form.Item name="bring_items" label={t("experiences.details.labels.guest_items")}>
        <MultipleSelectWithAddItemOption
          placeholder={t("experiences.details.placeholders.select_or_type")}
          options={experienceOptionsData?.bring_items}
          loading={isExperienceOptionsLoading}
          onChange={handleBringItemsChange}
          addItemLabel={t("experiences.details.labels.add_item")}
          notFoundLabel={t("experiences.details.labels.not_found")}
          disabledOptions={watchedIncludedItems}
          onAddItem={(name) => dispatch(addExperienceOption("bring_item", name))}
        />
      </Form.Item>

      <Form.Item name="languages" label={t("experiences.details.labels.languages")}>
        <Flex wrap gap="small" align="center">
          {(experienceOptionsData?.languages || []).map((language) => (
            <CheckableTag
              key={language.id}
              className="mr-xs mb-xxs"
              tag={language.name}
              checked={formLanguages.includes(language.id)}
              onChange={(checked) => handleLanguageChange(language, checked)}
            />
          ))}
        </Flex>
        <div className="mt-xs">
          <Text variant="caption" type="secondary">
            {t("experiences.details.labels.not_found_language")}{" "}
            <Text variant="link" onClick={() => setIsLanguageModalOpen(true)}>
              {t("experiences.details.labels.add_language")}
            </Text>
          </Text>
        </div>
      </Form.Item>

      <Form.Item name="skill_levels" label={t("experiences.details.labels.skill_level")}>
        <Flex wrap gap="small" align="center">
          {(experienceOptionsData?.skill_levels || []).map((level) => (
            <CheckableTag
              key={level.id}
              className="mr-xs mb-xxs"
              tag={level.name}
              checked={formSkillLevels.includes(level.id)}
              onChange={(checked) => handleSkillLevelChange(level, checked)}
            />
          ))}
        </Flex>
        <div className="mt-xs">
          <Text variant="caption" type="secondary">
            {t("experiences.details.labels.not_found_skill_level")}{" "}
            <Text variant="link" onClick={() => setIsSkillModalOpen(true)}>
              {t("experiences.details.labels.add_skill_level")}
            </Text>
          </Text>
        </div>
      </Form.Item>

      {renderAddOptionDialog({
        title: t("experiences.details.labels.add_language"),
        open: isLanguageModalOpen,
        onClose: () => { setIsLanguageModalOpen(false); setNewLanguage("") },
        onSubmit: handleAddLanguage,
        value: newLanguage,
        onChange: setNewLanguage,
        placeholder: t("experiences.details.placeholders.add_language"),
        inputRef: languageInputRef,
        loading: isAddingLanguage,
      })}

      {renderAddOptionDialog({
        title: t("experiences.details.labels.add_skill_level"),
        open: isSkillModalOpen,
        onClose: () => { setIsSkillModalOpen(false); setNewSkillLevel("") },
        onSubmit: handleAddSkillLevel,
        value: newSkillLevel,
        onChange: setNewSkillLevel,
        placeholder: t("experiences.details.placeholders.add_skill_level"),
        inputRef: skillInputRef,
        loading: isAddingSkillLevel,
      })}
    </Card>
  )
}

ExperienceDetails.propTypes = {
  form: PropTypes.object.isRequired,
}
